import { ReactElement, useCallback, useEffect, useState, useRef, memo } from 'react';
import { Modal } from '@components/common/modal/modal';

interface ILocationModalProps {
	isOpen: boolean;
	onClose: () => void;
	onLocationSuccess: (latitude: number, longitude: number) => void;
	onLocationError?: (error: string) => void;
	title?: string;
	message?: string;
	allowClose?: boolean;
}

const ERROR_MESSAGES = {
	PERMISSION_DENIED: 'Location permission denied. Please enable location access in your browser settings.',
	POSITION_UNAVAILABLE: 'Location information is unavailable.',
	TIMEOUT: 'Location request timed out.',
	NOT_SUPPORTED: 'Geolocation is not supported by your browser.',
	DEFAULT: 'An unknown error occurred while getting location.',
} as const;

const PERMISSION_STEPS = [
	"Click the padlock 🔒 icon in your browser's address bar",
	'Select “Allow” or “Always allow” for this site',
	'Then click “Refresh Page” to try again',
];

export const LocationModal = memo(
	({
		isOpen,
		onClose,
		onLocationSuccess,
		onLocationError,
		title = 'Location Access Required',
		message = 'We need your current location to provide accurate service. Please allow location access when prompted.',
		allowClose = true,
	}: ILocationModalProps): ReactElement | null => {
		const [isRequesting, setIsRequesting] = useState(false);
		const [errorMsg, setErrorMsg] = useState<string | null>(null);
		const [permissionState, setPermissionState] = useState<'granted' | 'denied' | 'prompt' | 'unknown'>('unknown');
		const hasLocationRef = useRef(false); // Track if location was successfully obtained

		const handleError = useCallback(
			(err: GeolocationPositionError | string): void => {
				let msg: string = ERROR_MESSAGES.DEFAULT;
				if (typeof err === 'string') {
					msg = err;
				} else {
					switch (err.code) {
						case err.PERMISSION_DENIED:
							msg = ERROR_MESSAGES.PERMISSION_DENIED;
							break;
						case err.POSITION_UNAVAILABLE:
							msg = ERROR_MESSAGES.POSITION_UNAVAILABLE;
							break;
						case err.TIMEOUT:
							msg = ERROR_MESSAGES.TIMEOUT;
							break;
					}
				}
				setErrorMsg(msg);
				onLocationError?.(msg);
			},
			[onLocationError]
		);

		const handleSuccess = useCallback(
			(position: GeolocationPosition): void => {
				// Prevent duplicate success calls
				if (hasLocationRef.current) return;

				const { latitude, longitude } = position.coords;
				hasLocationRef.current = true;
				setIsRequesting(false);
				setErrorMsg(null);

				// Call onLocationSuccess - this should close the modal via setShowLocationPermissionModal(false)
				// The parent component (siteInformation.tsx) handles closing the modal in handleLocationSuccess
				onLocationSuccess(latitude, longitude);
			},
			[onLocationSuccess]
		);

		const requestLocation = useCallback(() => {
			if (!navigator.geolocation) {
				handleError(ERROR_MESSAGES.NOT_SUPPORTED);
				return;
			}
			// Prevent multiple simultaneous requests or duplicate requests after success
			if (isRequesting || hasLocationRef.current) return;

			setIsRequesting(true);
			setErrorMsg(null);
			navigator.geolocation.getCurrentPosition(
				(position) => {
					setIsRequesting(false);
					handleSuccess(position);
				},
				(err) => {
					setIsRequesting(false);
					handleError(err);
				},
				{
					enableHighAccuracy: true,
					timeout: 15000,
					maximumAge: 0,
				}
			);
		}, [handleSuccess, handleError, isRequesting]);

		// Check permission state when modal opens
		useEffect(() => {
			if (!isOpen) {
				setIsRequesting(false);
				setErrorMsg(null);
				hasLocationRef.current = false; // Reset when modal closes
				return;
			}
			if ('permissions' in navigator) {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				(navigator.permissions as any).query({ name: 'geolocation' }).then((result: any) => {
					setPermissionState(result.state);
					if (result.state === 'granted') {
						requestLocation();
					} else if (result.state === 'prompt') {
						// Ask only once to avoid repeating popups
						requestLocation();
					} else {
						setErrorMsg(ERROR_MESSAGES.PERMISSION_DENIED);
					}

					// Watch for permission changes
					result.onchange = (): void => {
						setPermissionState(result.state);
						if (result.state === 'granted' && !hasLocationRef.current) {
							setErrorMsg(null);
							requestLocation();
						}
					};
				});
			} else {
				requestLocation();
			}
		}, [isOpen, requestLocation]);

		if (!isOpen) return null;

		return (
			<Modal
				modalId="locationPermissionModal"
				className="dialog-md dialog-center dialog-conform"
				modalOpen={isOpen}
				isEscAllow={false}
				onModalClose={allowClose ? onClose : undefined}
				modalTitle={title}
				aria-labelledby="location-modal-title"
				aria-describedby="location-modal-desc"
			>
				<div className="location-permission-content">
					<p id="location-modal-desc">{message}</p>

					{isRequesting && (
						<div className="location-loading">
							<div className="loading-spinner">
								<div className="spinner"></div>
								<p>Requesting your location...</p>
							</div>
						</div>
					)}

					{errorMsg && (
						<div className="location-error">
							<p className="error-message">{errorMsg}</p>
						</div>
					)}

					{permissionState === 'denied' && (
						<div className="permission-steps">
							<h4>How to enable location access:</h4>
							<ol>
								{PERMISSION_STEPS.map((step) => (
									<li key={step}>{step}</li>
								))}
							</ol>
						</div>
					)}
				</div>
			</Modal>
		);
	}
);

LocationModal.displayName = 'LocationModal';
